import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { useDecision } from "@optimizely/react-sdk"
import Timeago from "../common/Timeago"
import { useDeleteAnswersCommentMutation, useInfiniteAnswersComments } from "./Answer.state"
import { AnswerCommentsContainer, CommentItem, CommentItemBody, CommentMeta, LoadingLine } from "./AnswerComments.style"
import { ConfirmationModal, Spinner } from "../common"
import { Button } from "../App.styles"
import { useTeamDetailQuery } from '../teams/Teams.state'
import { ActionSpace } from './AnswerActions.style'
import { useRef } from "react"
import { useDebouncedCallback } from "use-debounce"

const TeamName = ({ id }) => {
    const { data, isFetching: isLoading } = useTeamDetailQuery(id)
    const teamName = data?.team?.name
    return isLoading && !teamName ? <LoadingLine /> : <strong className="d-block">{teamName}</strong>
}

const RenderCommentItem = ({ item, answerId }) => {
    const { t } = useTranslation()
    const { deleteComment, isDeletingComment } = useDeleteAnswersCommentMutation(answerId)
    return (
        <CommentItem>
            <CommentItemBody>
                <TeamName id={item.team_id} />
                <span dangerouslySetInnerHTML={{__html: item.comment}} />
                <CommentMeta>
                    <span>{item.likes} {t(`likes`)}</span>
                    <ActionSpace direction="horizontal">
                        <Timeago.Display time={item.created_at} />
                    </ActionSpace>
                </CommentMeta>
            </CommentItemBody>
            <div>
                <ConfirmationModal
                    title={t("Confirm Deletion")}
                    body={t("Are you sure you wish to delete?")}
                    confirmLabel={t("Delete")}
                    confirmAction={() => deleteComment(item)}
                    isLoading={isDeletingComment}
                >
                    <Button color="danger" size="sm">
                        <FontAwesomeIcon icon={faTimes} />
                    </Button>
                </ConfirmationModal>
            </div>
        </CommentItem>
    )
}

const InfiniteComments = ({ answerId }) => {
    const query = useInfiniteAnswersComments(answerId)
    const commentsBoxRef = useRef(null);

    const handleScroll = useDebouncedCallback(async (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target || {};
        const threshold = 10;

        if (scrollTop === 0) {
            return
        }

        if (commentsBoxRef.current) {        
            if (scrollHeight - scrollTop <= clientHeight + threshold) {
                query.fetchNextPage()
            }
        }
    }, 600)

    return (
        <AnswerCommentsContainer
            ref={commentsBoxRef}
            onScroll={handleScroll}
        >
            {
                query.data?.pages.map((page) => {
                    return page.comments.map((item) => {
                        return <RenderCommentItem item={item} answerId={answerId} key={item.id} />
                    })
                })
            }
            {
                query.isFetchingNextPage && (
                    <div className="p-2 text-center">
                        <Spinner size="0.875rem" />
                    </div>
                )
            }
        </AnswerCommentsContainer>
    )
}
const AnswerComments = ({ answerId }) => {
    const [decisionComments] = useDecision('comments')
    return (
        <div className="flex-grow-1 mb-2">
            {
                decisionComments?.enabled
                    ? <InfiniteComments answerId={answerId} />
                    : null
            }
        </div>
    )
}

export default AnswerComments